import { Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { AppdataContact } from '@cloudageone-schemas/AppdataContact';
import { GridDataSource } from 'app/shared/generic-grid/types/GridDataSource';
import { PAGE_NAME } from 'app/shared/generic-grid/types/page-name.constants';
import { RowDblClickEvent } from 'devextreme/ui/data_grid';
import { Item } from 'devextreme/ui/toolbar';
import { firstValueFrom } from 'rxjs';
import { filter as rxjsFilter } from 'rxjs/operators';
import { ContactService } from '../../services/contact.service';
import { ContactManageDialogComponent } from '../contact-manage-dialog/contact-manage-dialog.component';
import { ContactsFilters } from './constants/filters';

@Component({
	selector: 'app-contacts-grid',
	templateUrl: './contacts-grid.component.html',
	styleUrls: ['./contacts-grid.component.scss'],
	standalone: false
})
export class ContactsGridComponent {
	@Input() pageName: PAGE_NAME;
	@ViewChild('sidenav') sidenav!: MatSidenav;
	selectedRow?: AppdataContact;

	dataSource = new GridDataSource<AppdataContact>('id', (params) => firstValueFrom(this.contactService.findAndCountMany(params)));
	columns = [
		{ dataField: 'id', caption: 'ID', width: 100 },
		{ dataField: 'firstName', caption: 'First Name', width: 150 },
		{ dataField: 'lastName', caption: 'Last Name', width: 150 },
		{ dataField: 'title', caption: 'Title', width: 120 },
		{ dataField: 'company', caption: 'Company', width: 150 },
		{ dataField: 'email', caption: 'Email', width: 200 },
		{ dataField: 'officePhone', caption: 'Office Phone', width: 150 },
		{ dataField: 'officePhoneExtension', caption: 'Extension', width: 100 },
		{ dataField: 'mobileNumber', caption: 'Mobile Number', width: 150 },
		{ dataField: 'vendor.name', caption: 'Vendor', width: 200 },
		{ dataField: 'customer.company', caption: 'Customer', width: 200 },
		{ dataField: 'address.address1', caption: 'Address', width: 200 },
		{ dataField: 'address.city', caption: 'City', width: 150 },
		{ dataField: 'address.subdivision.abbreviation', caption: 'State', width: 80 },
		{ dataField: 'address.postalCode', caption: 'Postal Code', width: 120 },
		{ dataField: 'address.country.alpha_2Code', caption: 'Country', width: 100 }
	];
	filters = ContactsFilters;
	toolbarActions: Item[] = [];

	constructor(
		private readonly contactService: ContactService,
		private dialog: MatDialog
	) {
		this.buildToolbar();
	}

	addContact() {
		this.dialog
			.open<ContactManageDialogComponent, any, AppdataContact>(ContactManageDialogComponent, {
				disableClose: true,
				width: '1150px'
			})
			.afterClosed()
			.pipe(rxjsFilter((result): result is AppdataContact => !!result))
			.subscribe(() => this.reloadGrid());
	}

	onRowDblClick(event: RowDblClickEvent<AppdataContact>) {
		this.dialog
			.open<ContactManageDialogComponent, any, AppdataContact>(ContactManageDialogComponent, {
				disableClose: true,
				width: '1150px',
				data: { contact: event.data, viewOnly: true }
			})
			.afterClosed()
			.pipe(rxjsFilter((result): result is AppdataContact => !!result))
			.subscribe(() => this.reloadGrid());
	}

	onSelectionChanged(data: AppdataContact[]) {
		this.selectedRow = data[0];
		this.buildToolbar();
	}

	openSidenav() {
		if (!this.selectedRow) {
			return;
		}

		this.sidenav.open();
	}

	async closeSidenav() {
		this.sidenav.close();
	}

	private editContact() {
		if (!this.selectedRow) {
			return;
		}

		this.dialog
			.open<ContactManageDialogComponent, any, AppdataContact>(ContactManageDialogComponent, {
				disableClose: true,
				width: '1150px',
				data: { contact: this.selectedRow, viewOnly: true }
			})
			.afterClosed()
			.pipe(rxjsFilter((result): result is AppdataContact => !!result))
			.subscribe(() => this.reloadGrid());
	}

	private reloadGrid() {
		this.onSelectionChanged([]);
		this.dataSource.source.reload();
	}

	private buildToolbar() {
		const hasSelection = !!this.selectedRow;

		this.toolbarActions = [
			{
				widget: 'dxButton',
				options: {
					icon: 'edit',
					hint: 'Edit',
					disabled: !hasSelection,
					onClick: () => this.editContact()
				},
				location: 'after'
			},
			{
				widget: 'dxButton',
				options: {
					icon: 'info',
					hint: 'Info',
					disabled: !hasSelection,
					onClick: () => this.openSidenav()
				},
				location: 'after'
			}
		];
	}
}
